/**
 * The demo-request queue's view model — pure, and the one place its rules live.
 *
 * docs/PARSINADENT_DEMO_REQUEST_DESIGN.md §3.2 (T1–T9) and D5. Nothing here is
 * stored anywhere: «unanswered» is `status = new`, a request's age is `now −
 * created`, the SLA colour is derived from working days on every render, and
 * the timeline is a merge of three lists the CRM already holds. A number
 * nobody wrote is a fabricated fact, so none is written.
 */

import { PAGE_SIZE, SEARCH_MIN_CHARS } from '../constants/list';
import {
  CONTACT_MADE_OUTCOMES, DEMO_INTENT, DOCTOR_PAGE_INTENT, LEAD_INTENTS,
} from '../constants/leadPipeline';

export const DEMO_PAGE_SIZE = PAGE_SIZE;

const DAY = 86_400_000;
/** Iran has kept +03:30 all year since 2022; the platform runs on Tehran time. */
const TEHRAN_OFFSET_MS = 3.5 * 3_600_000;

// ── tabs and filters ────────────────────────────────────────────────────────

export const TABS = ['unanswered', 'inProgress', 'closed', 'all'];
export const OWNER_FILTERS = ['all', 'me', 'unassigned'];

/**
 * What each tab asks the CRM for. «در جریان» is the four open stages AFTER
 * `new` in one query — `openOnly` would include `new`, which is exactly the
 * tab next to it.
 */
const TAB_STATUSES = {
  unanswered: ['new'],
  inProgress: ['contacted', 'qualified', 'proposal', 'negotiation'],
  closed: ['won', 'lost'],
  all: null,
};

export function tabFilter(tab) {
  const statuses = TAB_STATUSES[tab];
  return statuses ? { statuses: [...statuses] } : {};
}

/** 'me' needs to know who «me» is; without a subject it cannot narrow, so it doesn't. */
export function ownerFilter(owner, mySub) {
  if (owner === 'me' && mySub) return { ownerId: mySub };
  if (owner === 'unassigned') return { unassignedOnly: true };
  return {};
}

/** A picker day ('YYYY-MM-DD', a Tehran calendar day) → the epoch ms it starts at. */
export function tehranDayStartMs(day) {
  if (!day || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const ms = new Date(`${day}T00:00:00+03:30`).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * The inclusive creation window, as the epoch-ms STRINGS the CRM takes. `to`
 * covers the whole of its day. A reversed range is swapped rather than
 * answered with an empty list the rep cannot explain.
 */
export function dateRangeFilter(from, to) {
  let start = tehranDayStartMs(from);
  let endDay = tehranDayStartMs(to);
  if (start != null && endDay != null && start > endDay) [start, endDay] = [endDay, start];
  const out = {};
  if (start != null) out.createdFromMs = String(start);
  if (endDay != null) out.createdToMs = String(endDay + DAY - 1);
  return out;
}

/** The search box's contribution: ≥ 2 characters or nothing (DESIGN_CONCEPT §8). */
export function queryFilter(q) {
  const trimmed = String(q || '').trim();
  return trimmed.length >= SEARCH_MIN_CHARS ? { query: trimmed.slice(0, 120) } : {};
}

/** Every variable `crmLeads` is called with, from the URL view. */
export function leadsVariables(view, { mySub, pageSize = DEMO_PAGE_SIZE } = {}) {
  const page = Math.max(1, Number(view?.page) || 1);
  const intent = LEAD_INTENTS.includes(view?.intent) ? view.intent : DEMO_INTENT;
  return {
    intent,
    // A doctor-page queue shows whom to call; the CRM resolves the mobile only
    // when asked (one GetCustomer per customer), so a demo page never pays it.
    ...(intent === DOCTOR_PAGE_INTENT ? { withMobile: true } : {}),
    ...tabFilter(view?.tab || 'unanswered'),
    ...ownerFilter(view?.owner, mySub),
    ...dateRangeFilter(view?.from, view?.to),
    ...queryFilter(view?.q),
    limit: pageSize,
    offset: (page - 1) * pageSize,
  };
}

/** Is anything beyond the tab narrowing the list? (Filtered-empty ≠ truly-empty.) */
export function isFiltered(view) {
  return !!((view?.owner && view.owner !== 'all') || view?.from || view?.to
    || String(view?.q || '').trim().length >= SEARCH_MIN_CHARS);
}

export function pageCount(total, pageSize = DEMO_PAGE_SIZE) {
  return Math.max(1, Math.ceil((Number(total) || 0) / pageSize));
}

// ── age and the «one working day» promise ───────────────────────────────────

const toMs = (value) => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Working days (Saturday–Thursday, Tehran time) between two instants, as a
 * fraction. Friday contributes nothing. No holiday calendar: this drives a
 * colour, not a contract.
 */
export function workingDaysBetween(startMs, endMs) {
  const a = toMs(startMs);
  const b = toMs(endMs);
  if (a == null || b == null || b <= a) return 0;
  // Shift into "Tehran wall-clock as UTC" so day boundaries and weekdays are Tehran's.
  let t = a + TEHRAN_OFFSET_MS;
  const end = b + TEHRAN_OFFSET_MS;
  const WEEK = 7 * DAY;
  const weeks = Math.floor((end - t) / WEEK);
  let working = weeks * 6 * DAY;
  t += weeks * WEEK;
  while (t < end) {
    const dayStart = Math.floor(t / DAY) * DAY;
    const segmentEnd = Math.min(dayStart + DAY, end);
    if (new Date(dayStart).getUTCDay() !== 5) working += segmentEnd - t;   // 5 = Friday
    t = segmentEnd;
  }
  return working / DAY;
}

export const AMBER_AFTER_WORKING_DAYS = 1;
export const RED_AFTER_WORKING_DAYS = 3;

/**
 * The SLA tone of a request: 'danger' past three working days, 'warn' past
 * one, null otherwise. Only an UNANSWERED (`new`) request is on the clock —
 * once someone has made contact the promise was kept or broken, and colouring
 * an in-progress deal by its age would be noise.
 */
export function ageTone(lead, now = Date.now()) {
  if (!lead || lead.status !== 'new') return null;
  const days = workingDaysBetween(lead.createdAtMs, now);
  if (days > RED_AFTER_WORKING_DAYS) return 'danger';
  if (days > AMBER_AFTER_WORKING_DAYS) return 'warn';
  return null;
}

/**
 * How long the first real contact took, in ms, or null when there has been
 * none. Prefers the earliest interaction whose outcome is a real conversation;
 * without the interaction list (the table row), falls back to the lead's own
 * `new → contacted` move — which is only knowable while the lead still sits in
 * `contacted`, because `statusChangedMs` is the LATEST move.
 */
export function firstResponseMs(lead, interactions = null) {
  const created = toMs(lead?.createdAtMs);
  if (created == null) return null;
  if (Array.isArray(interactions)) {
    const times = interactions
      .filter((i) => CONTACT_MADE_OUTCOMES.includes(i.outcome))
      .map((i) => toMs(i.occurredAtMs))
      .filter((ms) => ms != null && ms >= created);
    if (times.length) return Math.min(...times) - created;
  }
  if (lead.status === 'contacted') {
    const changed = toMs(lead.statusChangedMs);
    if (changed != null && changed >= created) return changed - created;
  }
  return null;
}

// ── follow-ups and the timeline ─────────────────────────────────────────────

export const isOverdue = (followUp, now = Date.now()) =>
  followUp?.status === 'open' && toMs(followUp.dueAtMs) != null && toMs(followUp.dueAtMs) < now;

/** The follow-ups that belong to this lead (the CRM lists them per customer). */
export const followUpsOfLead = (followUps, leadId) =>
  (followUps || []).filter((f) => f.leadId === leadId);

/**
 * Interactions, this lead's follow-ups and the customer's notes, merged
 * newest first. A follow-up sits at its DUE time — what matters about «جلسهٔ
 * دمو» is when it is, not when it was typed — so an upcoming session leads the
 * timeline. Items without a time sink to the end rather than vanishing.
 */
export function buildTimeline({ interactions = [], followUps = [], notes = [] } = {}) {
  const items = [
    ...interactions.map((item) => ({ kind: 'interaction', id: `i-${item.id}`, atMs: toMs(item.occurredAtMs) ?? toMs(item.recordedAtMs), item })),
    ...followUps.map((item) => ({ kind: 'followUp', id: `f-${item.id}`, atMs: toMs(item.dueAtMs) ?? toMs(item.createdAtMs), item })),
    ...notes.map((item) => ({ kind: 'note', id: `n-${item.id}`, atMs: toMs(item.createdAtMs), item })),
  ];
  return items.sort((x, y) => (y.atMs ?? -Infinity) - (x.atMs ?? -Infinity));
}

// ── the request's answers ───────────────────────────────────────────────────

/** The answers that have a closed vocabulary, and the catalogue prefix of their labels. */
export const ENUM_ANSWERS = {
  practiceType: 'demoRequests.answers.practiceType',
  dentistCount: 'demoRequests.answers.dentistCount',
  demoMode: 'demoRequests.answers.demoMode',
  preferredTime: 'demoRequests.answers.preferredTime',
  currentSystem: 'demoRequests.answers.currentSystem',
};

/** `'2-5'` is not a usable catalogue key segment in every tool; these are. */
export const DENTIST_COUNT_KEY = { 1: 'one', '2-5': 'twoToFive', '6-15': 'sixToFifteen', '16+': 'sixteenPlus' };

const ANSWER_ORDER = [
  'practiceType', 'dentistCount', 'organization', 'city', 'province',
  'demoMode', 'preferredTime', 'currentSystem',
];

/**
 * The labelled answers present on a request, in the order a rep reads them.
 * `valueKey` is the catalogue key for an enum answer (null when the value is
 * one this build has not been taught — the caller echoes `value` raw), and
 * free-text answers carry `value` only. The visitor's message is NOT here; it
 * renders as its own block.
 */
export function detailRows(details) {
  if (!details) return [];
  const rows = [];
  for (const field of ANSWER_ORDER) {
    const value = details[field];
    if (value == null || String(value).trim() === '') continue;
    const prefix = ENUM_ANSWERS[field];
    let valueKey = null;
    if (prefix) {
      const segment = field === 'dentistCount' ? DENTIST_COUNT_KEY[value] : value;
      valueKey = segment ? `${prefix}.${segment}` : null;
    }
    rows.push({ field, labelKey: `demoRequests.answers.label.${field}`, value: String(value), valueKey });
  }
  return rows;
}

/** The UTM pairs present, as `[name, value]`, in the conventional order. */
export function utmPairs(utm) {
  if (!utm) return [];
  return ['source', 'medium', 'campaign', 'content', 'term']
    .filter((k) => utm[k] != null && String(utm[k]).trim() !== '')
    .map((k) => [k, String(utm[k])]);
}

/**
 * A landing URL safe to put in an `href`: http(s) only. The public schema
 * already enforces that; this is the second line, because the value is
 * visitor-supplied and an `href` is where `javascript:` would run.
 */
export function safeHttpUrl(value) {
  if (!value) return null;
  try {
    const url = new URL(String(value));
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}

/** A lead's contact points, split into what the drawer renders: mobiles/phones and e-mails. */
export function contactsOf(customerDetail) {
  const points = (customerDetail?.contactPoints || []).filter((p) => p.isActive !== false);
  const phones = points.filter((p) => ['mobile', 'landline'].includes(p.kind));
  const emails = points.filter((p) => p.kind === 'email');
  const byPrimary = (x, y) => Number(!!y.isPrimary) - Number(!!x.isPrimary);
  return { phones: phones.sort(byPrimary), emails: emails.sort(byPrimary) };
}

/**
 * The status moves this screen offers: exactly the service's
 * `allowedNextStatuses`, minus `won` straight from `new` — that path is the
 * self-serve purchase, and a demo request is by definition a deal a human
 * walks through (§3.2 T7). `lost` is split out because it needs a reason and
 * gets its own dialog.
 */
export function offeredMoves(lead) {
  const allowed = Array.isArray(lead?.allowedNextStatuses) ? lead.allowedNextStatuses : [];
  const moves = allowed.filter((s) => !(lead.status === 'new' && s === 'won'));
  return {
    advance: moves.filter((s) => s !== 'lost'),
    canLose: moves.includes('lost'),
  };
}

/** Other customers reachable on the same number, excluding the one this lead belongs to. */
export const otherRequesters = (matches, customerId) =>
  (matches || []).filter((c) => c.id !== customerId);

// ── doctor-page requests (intent doctor_page_appointment) ───────────────────

const SLUG = /^[a-z0-9-]{3,40}$/;

/**
 * The public page a request was sent from: the lead's `subjectRef`
 * ('doctor_page:<slug>') first — the CRM's own key — then the details' slug.
 * Anything not a valid slug is no page at all, never a guessed link.
 */
export function doctorPageSlug(lead) {
  const ref = String(lead?.subjectRef || '');
  if (ref.startsWith('doctor_page:')) {
    const slug = ref.slice('doctor_page:'.length);
    if (SLUG.test(slug)) return slug;
  }
  const fromDetails = lead?.requestDetails?.slug;
  return fromDetails && SLUG.test(fromDetails) ? fromDetails : null;
}

/** `${base}/<slug>`, or null — a link built only from a validated slug. */
export function doctorPageUrl(lead, base) {
  const slug = doctorPageSlug(lead);
  if (!slug || !base) return null;
  return `${String(base).replace(/\/+$/, '')}/${slug}`;
}

export const isDoctorPageLead = (lead) => lead?.intent === DOCTOR_PAGE_INTENT;

/**
 * A doctor-page request's labelled facts for the drawer, in reading order.
 * Same row shape as `detailRows`; the page link is rendered by the caller.
 */
export function pageRequestRows(details) {
  if (!details) return [];
  const rows = [];
  const push = (field, value, valueKey = null) => {
    if (value == null || String(value).trim() === '') return;
    rows.push({ field, labelKey: `demoRequests.pageLeads.answers.${field}`, value: String(value), valueKey });
  };
  push('doctorDisplayName', details.doctorDisplayName);
  push('councilNumber', details.councilNumber);
  push('preferredChannel', details.preferredChannel,
    details.preferredChannel ? `demoRequests.channel.${details.preferredChannel}` : null);
  push('preferredTime', details.preferredTime,
    details.preferredTime ? `${ENUM_ANSWERS.preferredTime}.${details.preferredTime}` : null);
  return rows;
}
